import React from "react";
import Link from "next/link";
import BlogCoverArt from "@/src/features/blog/components/BlogCoverArt";
import { formatBlogDate } from "@/src/features/blog/blogVisuals";

interface IBlogCardProps {
  slug: string;
  title: string;
  banner_image?: string | null;
  published_at?: string;
  /** List page cards show a "Read article →" affordance. */
  showReadMore?: boolean;
  className?: string;
}

/**
 * Grid blog card (home teaser + /blogs list). Uses a generated tinted cover
 * (with the real banner image overlaid when present, on all viewports).
 * The list endpoint carries no excerpt/read-time, so the card intentionally
 * shows only date + title.
 */
function BlogCard({
  slug,
  title,
  banner_image,
  published_at,
  showReadMore = false,
  className = "",
}: IBlogCardProps) {
  const date = formatBlogDate(published_at);

  return (
    <Link
      href={`/blogs/${slug}`}
      className={`group block text-inherit transition-transform duration-500 ease-out hover:-translate-y-1.5 ${className}`}
    >
      <BlogCoverArt
        seed={slug}
        title={title}
        imageUrl={banner_image}
        eyebrow="TRAVELA · GUIDE"
        className="aspect-video rounded-2xl"
      />

      {date ? (
        <div className="mt-4 flex items-center gap-2.5 text-12 font-medium text-ink-gray">
          <span>{date}</span>
        </div>
      ) : null}

      <h3 className="mt-2 font-sans text-20 font-bold leading-[1.32] tracking-[-0.02em] text-ink-black [text-wrap:pretty] transition-colors duration-300 group-hover:text-brand-500">
        {title}
      </h3>

      {showReadMore ? (
        <span className="mt-3 inline-block font-sans text-13 font-semibold text-brand-500">
          Read article →
        </span>
      ) : null}
    </Link>
  );
}

export default BlogCard;
